import React, { ReactNode, useMemo } from 'react';
import { HStack } from '@croquiscom/zds-core';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { cx } from '@emotion/css';
import { css, SerializedStyles } from '@emotion/react';

import { resetButton, Typography, useBrandStyle } from '@common/styles';

export enum ButtonSize {
  LARGE = 'LARGE',
  MEDIUM = 'MEDIUM',
  SMALL = 'SMALL',
  XSMALL = 'XSMALL',
}
export enum ButtonColor {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  SECONDARY_GRAY = 'SECONDARY_GRAY',
  TERTIARY = 'TERTIARY',
  TERTIARY_GRAY = 'TERTIARY_GRAY',
}

/*
button_icon은 button_color에 의해 color값이 정해지므로
영향받지 않는 아이콘을 넣고 싶으면 children을 통해 주입할것
*/
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  css?: SerializedStyles;
  button_size?: ButtonSize;
  button_color?: ButtonColor;
  button_icon?: ReactNode;
  fill?: boolean;
}
export const Button: React.FC<React.PropsWithChildren<ButtonProps>> = ({
  children,
  css: custom_css,
  button_size,
  button_color,
  button_icon,
  fill = false,
  ...otherProps
}) => {
  const { brand_color } = useBrandStyle();
  const typoSize = useMemo(() => {
    switch (button_size) {
      case ButtonSize.LARGE:
      case ButtonSize.MEDIUM:
        return Typography.BODY_15_BOLD;
      case ButtonSize.SMALL:
        return Typography.BODY_13_SEMIBOLD;
      case ButtonSize.XSMALL:
        return Typography.CAPTION_12_SEMIBOLD;
      default:
        return '';
    }
  }, [button_size]);

  const buttonSize = useMemo(() => {
    switch (button_size) {
      case ButtonSize.LARGE:
        return css`
          height: 50px;
          border-radius: 25px;
          padding: 0 20px;
        `;
      case ButtonSize.MEDIUM:
        return css`
          height: 42px;
          border-radius: 21px;
          padding: 0 14px;
        `;
      case ButtonSize.SMALL:
        return css`
          height: 32px;
          border-radius: 16px;
          padding: 0 10px;
        `;
      case ButtonSize.XSMALL:
        return css`
          height: 24px;
          border-radius: 12px;
          padding: 0 12px;
        `;
      default:
        return css``;
    }
  }, [button_size]);

  const buttonColor = useMemo(() => {
    switch (button_color) {
      case ButtonColor.PRIMARY:
        return css`
          background: ${vars.color.fillPrimaryInverse};
          color: ${vars.color.textStrongInverse};
          .button_icon {
            svg {
              color: ${vars.color.textStrongInverse};
            }
          }
        `;
      case ButtonColor.SECONDARY:
        return css`
          background: ${brand_color.primary};
          color: ${vars.color.staticWhite};
          .button_icon {
            svg {
              color: ${vars.color.staticWhite};
            }
          }
        `;
      case ButtonColor.SECONDARY_GRAY:
        return css`
          border: 1px solid ${vars.color.borderStrong};
          color: ${vars.color.textStrong};
          .button_icon {
            svg {
              color: ${vars.color.textStrong};
            }
          }
        `;
      case ButtonColor.TERTIARY:
        return css`
          border: 1px solid ${brand_color.primary};
          color: ${brand_color.primary};
          .button_icon {
            svg {
              color: ${brand_color.primary};
            }
          }
        `;
      case ButtonColor.TERTIARY_GRAY:
        return css`
          border: 1px solid ${vars.color.borderSecondary};
          color: ${vars.color.textStrong};
          .button_icon {
            svg {
              color: ${vars.color.textStrong};
            }
          }
        `;
      default:
        return css`
          color: ${vars.color.textSecondary};
        `;
    }
  }, [button_color, brand_color]);

  return (
    <button
      type='button'
      {...otherProps}
      css={css`
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        width: ${fill ? '100%' : 'auto'};

        ${resetButton}
        ${buttonSize}
        ${buttonColor}

        &:disabled {
          background: ${vars.color.fillDisabled};
          color: ${vars.color.textDisabled};

          svg {
            color: ${vars.color.textDisabled};
          }
        }

        ${custom_css}
      `}
    >
      <HStack
        spacing={2}
        alignment='center'
        css={css`
          .button_icon {
            display: inline-flex;
          }
          .button_text {
            display: inline-flex;
            align-items: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}
      >
        {button_icon && <span className='button_icon'>{button_icon}</span>}
        <span className={cx('button_text', typoSize)}>{children}</span>
      </HStack>
    </button>
  );
};
